import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';
import { withModalWindow } from '@ui/modals/ModalWindowMol.tsx';
import { getThemeVal } from '@utils/styled-component-tools.ts';

import { ButtonBase } from '../styles.ts';
import StepView from './StepView.tsx';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 16px 16px;
  width: 100%;

  .-desktop & {
    width: 351px;
  }
`;

const NextStepButton = styled(ButtonBase)`
  background-color: ${({ disabled }) => (disabled ? 'var(--color-primaryButton-30)' : 'var(--color-primaryButton)')};
  border: none;
  border-radius: ${getThemeVal('radii.lg', '8px')};
  color: var(--color-primaryButtonText);
  margin-top: 8px;
  &:not([disabled]):hover {
    background-color: var(--color-primaryButtonHover);
  }

  .-desktop & {
    margin-bottom: 32px;
  }
`;

const PickerSteps = {
  SECURITY: 'security',
  IMMIGRATION: 'immigration',
};

const LanePickerView = ({
  widgetState,
  T,
  onSubmitClick,
  onModalDismiss,
  setModalBackButtonAction,
  setModalDismissAction,
  highZoomMaxWidth,
}) => {
  const [state, setWidgetState] = useState(widgetState.getState());
  const [step, setStep] = useState(PickerSteps.SECURITY); // set initial step to security check
  const [hasImmigrationCheck, setHasImmigrationCheck] = useState(
    state.immigrationLanes && state.immigrationLanes.length,
  );

  useEffect(() => widgetState.addCallback(setWidgetState), [widgetState]);

  useEffect(() => {
    setModalDismissAction(onModalDismiss);
  }, []);

  useEffect(() => {
    setHasImmigrationCheck(state.immigrationLanes && state.immigrationLanes.length);
  }, [state.immigrationLanes]);

  const onSecurityLaneClick = id => {
    const newLanes = state.securityLanes.map(type => ({
      ...type,
      default: type.id === id ? !type.default : type.default,
    }));
    setWidgetState({ ...state, securityLanes: newLanes });
    widgetState.update({ securityLanes: newLanes });
  };

  const onImmigrationLaneClick = id => {
    const newLanes = state.immigrationLanes.map(type => ({
      ...type,
      default: type.id === id ? !type.default : type.default,
    }));
    setWidgetState({ ...state, immigrationLanes: newLanes });
    widgetState.update({ immigrationLanes: newLanes });
  };

  const onNextStepClick = () => {
    setStep(PickerSteps.IMMIGRATION);
    setModalBackButtonAction(onPrevStepClick);
  };

  const onPrevStepClick = () => {
    setStep(PickerSteps.SECURITY);
    setModalBackButtonAction(null);
  };

  const securityLaneView = highZoomMaxWidth => (
    <StepView
      title={T('getDirectionsFromTo:You’ll be passing through Security')}
      highZoomMaxWidth={highZoomMaxWidth}
      subtitle={
        T(
          'getDirectionsFromTo:To find your quickest route, please select the security categories you are able to pass through',
        ) +
        ' ' +
        T('getDirectionsFromTo:Security wait times are an approximation')
      }
      categories={state.securityLanes}
      onCategoryClick={onSecurityLaneClick}
    />
  );

  const immigrationLaneView = highZoomMaxWidth => (
    <StepView
      title={T('getDirectionsFromTo:You’ll be passing through Immigration and Customs')}
      highZoomMaxWidth={highZoomMaxWidth}
      subtitle={
        T(
          'getDirectionsFromTo:To find the quickest route, please select the immigration and customs categories you are able to pass through',
        ) +
        ' ' +
        T('getDirectionsFromTo:Security wait times are an approximation')
      }
      categories={state.immigrationLanes}
      onCategoryClick={onImmigrationLaneClick}
    />
  );

  let view = securityLaneView;

  switch (step) {
    case PickerSteps.SECURITY:
      view = securityLaneView(highZoomMaxWidth);
      break;
    case PickerSteps.IMMIGRATION:
      view = immigrationLaneView(highZoomMaxWidth);
      break;
  }

  const getNextStepButton = () => {
    const hasSecurityChecked = state.securityLanes.some(el => el.default === true);

    if (step !== PickerSteps.IMMIGRATION && hasImmigrationCheck) {
      return (
        <NextStepButton onClick={onNextStepClick} disabled={!hasSecurityChecked}>
          {T('getDirectionsFromTo:Next')}
        </NextStepButton>
      );
    }

    return (
      <NextStepButton onClick={onSubmitClick} data-testid="GetMyRouteButton">
        {T('getDirectionsFromTo:Get My Route')}
      </NextStepButton>
    );
  };
  const nextStepButton = getNextStepButton();

  return (
    <Wrapper>
      <Icon name="security" cdn width={48} height={48} />
      {view}
      {nextStepButton}
    </Wrapper>
  );
};

export default withModalWindow(LanePickerView, '[data-testid=GetMyRouteButton]');
